import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { createGig, updateGig, fetchGig, fetchCategories, uploadFile, fileUrl } from "@/lib/api";
import { useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { X, Upload, Youtube, ImagePlus } from "lucide-react";

const MAX_IMAGES = 9;
const emptyPkg = (name) => ({ name, title: "", price: 0, delivery_days: 3, revisions: 1, features: [] });

// Parse various YouTube URL formats into a video id
function parseYouTubeId(url) {
  if (!url) return null;
  try {
    const u = new URL(url.startsWith("http") ? url : "https://" + url);
    if (u.hostname.includes("youtu.be")) {
      const id = u.pathname.replace("/", "");
      return id || null;
    }
    if (u.hostname.includes("youtube.com")) {
      if (u.pathname === "/watch") return u.searchParams.get("v");
      if (u.pathname.startsWith("/embed/") || u.pathname.startsWith("/shorts/") || u.pathname.startsWith("/live/")) return u.pathname.split("/")[2];
    }
  } catch {}
  return null;
}

export default function CreateGigPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "",
    tags: "",
    images: [],
    video_url: "",
    packages: [emptyPkg("Basic"), emptyPkg("Standard"), emptyPkg("Premium")],
  });

  useEffect(() => { fetchCategories().then(setCats).catch(() => {}); }, []);
  useEffect(() => {
    if (!editId || !user) return;
    fetchGig(editId).then((g) => {
      if (g.seller_id !== user.user_id) { toast.error("You can only edit your own gigs"); navigate("/dashboard"); return; }
      const byName = Object.fromEntries((g.packages || []).map((p) => [p.name, p]));
      setForm({
        title: g.title || "",
        description: g.description || "",
        category: g.category || "",
        tags: (g.tags || []).join(", "),
        images: g.images?.length ? g.images : (g.cover_image ? [g.cover_image] : []),
        video_url: g.video_url || "",
        packages: ["Basic", "Standard", "Premium"].map((n) => ({ ...emptyPkg(n), ...(byName[n] || {}), features: byName[n]?.features || [] })),
      });
    }).catch(() => { toast.error("Gig not found"); navigate("/dashboard"); });
  }, [editId, user, navigate]);
  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);

  const updatePkg = (i, key, val) => {
    const packages = [...form.packages];
    packages[i] = { ...packages[i], [key]: val };
    setForm({ ...form, packages });
  };

  const setPkgFeatures = (i, txt) => {
    const packages = [...form.packages];
    packages[i] = { ...packages[i], features: txt.split("\n") };
    setForm({ ...form, packages });
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = MAX_IMAGES - form.images.length;
    if (remaining <= 0) { toast.error(`Maximum ${MAX_IMAGES} images allowed`); return; }
    const toUpload = files.slice(0, remaining);
    if (files.length > remaining) toast.warning(`Only ${remaining} more image(s) allowed — uploading first ${remaining}`);
    setUploading(true);
    try {
      const paths = [];
      for (const f of toUpload) {
        const r = await uploadFile(f);
        paths.push(r.path);
      }
      setForm((f) => ({ ...f, images: [...f.images, ...paths] }));
      toast.success(`${paths.length} image(s) uploaded`);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (i) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));
  };

  const ytId = parseYouTubeId(form.video_url);
  const ytThumbs = ytId ? [`https://img.youtube.com/vi/${ytId}/maxresdefault.jpg`, `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`] : [];
  const thumbIsCover = ytThumbs.includes(form.images[0]);

  const useVideoThumb = () => {
    const img = new Image();
    img.onload = () => {
      const url = img.naturalWidth > 120 ? ytThumbs[0] : ytThumbs[1];
      const rest = form.images.filter((x) => !ytThumbs.includes(x));
      if (rest.length >= MAX_IMAGES) { toast.error(`Remove an image first (max ${MAX_IMAGES})`); return; }
      setForm((f) => ({ ...f, images: [url, ...f.images.filter((x) => !ytThumbs.includes(x))] }));
      toast.success("Video thumbnail set as cover");
    };
    img.onerror = () => toast.error("Couldn't load the video thumbnail");
    img.src = ytThumbs[0];
  };

  const submit = async () => {
    if (!form.title || !form.description || !form.category) {
      toast.error("Fill title, description and category");
      return;
    }
    if (form.video_url && !parseYouTubeId(form.video_url)) {
      toast.error("Video URL must be a valid YouTube link");
      return;
    }
    for (const p of form.packages) {
      if (!p.title || !p.price) { toast.error(`Fill ${p.name} package details`); return; }
    }
    setSaving(true);
    try {
      const body = {
        title: form.title,
        description: form.description,
        category: form.category,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        images: form.images,
        cover_image: form.images[0],
        video_url: form.video_url.trim(),
        packages: form.packages.map((p) => ({ ...p, price: Number(p.price), delivery_days: Number(p.delivery_days), revisions: Number(p.revisions), features: p.features.map((s) => s.trim()).filter(Boolean) })),
      };
      const gig = editId ? await updateGig(editId, body) : await createGig(body);
      toast.success(editId ? "Gig updated" : "Gig published");
      navigate(`/gig/${gig.gig_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (editId ? "Failed to save" : "Failed to publish"));
    } finally { setSaving(false); }
  };

  if (loading || !user) return <div className="p-16 text-center">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-10">
      <div className="overline text-primary mb-2">{editId ? "◆ EDIT GIG" : "◆ NEW GIG"}</div>
      <h1 data-testid="gig-form-heading" className="text-4xl font-bold tracking-tight mb-8">{editId ? "Edit your service" : "Publish your service"}</h1>

      <div className="space-y-8">
        <Section title="◆ Basics">
          <label className="overline block mb-2">Title</label>
          <Input
            data-testid="gig-title-input"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="I will design a distinctive brand identity"
            className="rounded-none border-foreground/20 mb-5"
          />
          <label className="overline block mb-2">Description</label>
          <Textarea
            data-testid="gig-description-input"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="What's included? What makes you different?"
            rows={6}
            className="rounded-none border-foreground/20 mb-5"
          />
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="overline block mb-2">Category</label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="gig-category-select" className="rounded-none border-foreground/20">
                  <SelectValue placeholder="Select category"/>
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {cats.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="overline block mb-2">Tags (comma separated)</label>
              <Input
                data-testid="gig-tags-input"
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                placeholder="logo, branding, identity"
                className="rounded-none border-foreground/20"
              />
            </div>
          </div>
        </Section>

        <Section title={`◆ Images (${form.images.length}/${MAX_IMAGES})`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {form.images.map((im, i) => (
              <div key={i} className="relative aspect-video border border-foreground/15 group">
                <img src={fileUrl(im)} className="w-full h-full object-cover" alt=""/>
                {i === 0 && <span className="absolute top-1 left-1 bg-primary text-primary-foreground mono text-[10px] px-1.5 py-0.5">COVER</span>}
                <button
                  onClick={() => removeImage(i)}
                  data-testid={`remove-image-${i}`}
                  className="absolute top-1 right-1 bg-foreground text-background p-1 opacity-0 group-hover:opacity-100"
                ><X className="w-3 h-3"/></button>
              </div>
            ))}
            {form.images.length < MAX_IMAGES && (
              <label
                data-testid="upload-images-label"
                className="aspect-video border-2 border-dashed border-foreground/30 flex flex-col items-center justify-center cursor-pointer hover:border-foreground hover:bg-muted transition-colors"
              >
                <Upload className="w-5 h-5 mb-1"/>
                <span className="mono text-xs">{uploading ? "UPLOADING..." : "UPLOAD"}</span>
                <input data-testid="upload-images-input" type="file" accept="image/*" multiple hidden onChange={handleUpload}/>
              </label>
            )}
          </div>
          <p className="text-xs text-muted-foreground mono">
            PNG / JPG / WEBP · MAX 8MB EACH · UP TO {MAX_IMAGES} IMAGES · FIRST IMAGE = COVER
          </p>
        </Section>

        <Section title="◆ Video (optional)">
          <label className="overline block mb-2">◆ YouTube URL</label>
          <div className="flex gap-2">
            <div className="flex items-center px-3 border border-foreground/20 border-r-0 bg-muted">
              <Youtube className="w-4 h-4"/>
            </div>
            <Input
              data-testid="gig-video-url"
              value={form.video_url}
              onChange={(e) => setForm({ ...form, video_url: e.target.value })}
              placeholder="https://youtube.com/watch?v=…  or  https://youtu.be/…"
              className="rounded-none border-foreground/20 flex-1"
            />
          </div>
          {form.video_url && parseYouTubeId(form.video_url) && (
            <div data-testid="video-preview" className="mt-4 aspect-video border border-foreground/15">
              <iframe
                src={`https://www.youtube.com/embed/${parseYouTubeId(form.video_url)}`}
                title="Video preview"
                className="w-full h-full"
                frameBorder="0"
                allowFullScreen
              />
            </div>
          )}
          {ytId && (
            <button
              type="button"
              data-testid="use-video-thumb-btn"
              onClick={useVideoThumb}
              disabled={thumbIsCover}
              className="mt-3 inline-flex items-center gap-2 border border-foreground px-4 py-2 mono text-xs hover:bg-primary hover:border-primary transition-colors disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:border-foreground"
            >
              <ImagePlus className="w-4 h-4"/> {thumbIsCover ? "VIDEO THUMBNAIL IS YOUR COVER" : "USE VIDEO THUMBNAIL AS COVER"}
            </button>
          )}
          {form.video_url && !parseYouTubeId(form.video_url) && (
            <p className="mono text-xs text-destructive mt-2">Not a valid YouTube URL</p>
          )}
          <p className="text-xs text-muted-foreground mono mt-2">Paste a Public or Unlisted YouTube link — we'll embed it on your gig page. (Private videos can't be shown to buyers.)</p>
        </Section>

        <Section title="◆ Packages">
          <div className="grid md:grid-cols-3 gap-4">
            {form.packages.map((p, i) => (
              <div key={p.name} className="border border-foreground/15 p-5 space-y-3">
                <div className="mono text-xs font-bold overline text-primary">◆ {p.name}</div>
                <Input
                  data-testid={`pkg-${p.name}-title`}
                  value={p.title} onChange={(e) => updatePkg(i, "title", e.target.value)}
                  placeholder="Package title" className="rounded-none border-foreground/20"
                />
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="mono text-[10px] opacity-70 uppercase block mb-1">◆ Price (USD)</label>
                    <Input
                      data-testid={`pkg-${p.name}-price`}
                      type="number" min="0" value={p.price} onChange={(e) => updatePkg(i, "price", e.target.value)}
                      placeholder="$" className="rounded-none border-foreground/20 mono"
                    />
                  </div>
                  <div>
                    <label className="mono text-[10px] opacity-70 uppercase block mb-1">◆ Delivery (days)</label>
                    <Input
                      data-testid={`pkg-${p.name}-delivery`}
                      type="number" min="1" value={p.delivery_days} onChange={(e) => updatePkg(i, "delivery_days", e.target.value)}
                      placeholder="days" className="rounded-none border-foreground/20 mono"
                    />
                  </div>
                  <div>
                    <label className="mono text-[10px] opacity-70 uppercase block mb-1">◆ Revisions</label>
                    <Input
                      data-testid={`pkg-${p.name}-revs`}
                      type="number" min="0" value={p.revisions} onChange={(e) => updatePkg(i, "revisions", e.target.value)}
                      placeholder="rev." className="rounded-none border-foreground/20 mono"
                    />
                  </div>
                </div>
                <Textarea
                  data-testid={`pkg-${p.name}-features`}
                  value={p.features.join("\n")}
                  onChange={(e) => setPkgFeatures(i, e.target.value)}
                  placeholder={"One feature per line\nPress Enter for the next one"}
                  rows={4}
                  className="rounded-none border-foreground/20 text-sm"
                />
              </div>
            ))}
          </div>
        </Section>

        <div className="flex gap-3 justify-end">
          <button onClick={() => navigate("/dashboard")} className="border border-foreground px-6 py-3 mono text-xs">CANCEL</button>
          <button
            data-testid="publish-gig-btn"
            onClick={submit}
            disabled={saving}
            className="bg-primary text-primary-foreground px-8 py-3 mono text-xs hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? (editId ? "SAVING…" : "PUBLISHING…") : (editId ? "SAVE CHANGES" : "PUBLISH GIG")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-foreground/15 bg-card p-6">
      <div className="overline mb-4">{title}</div>
      {children}
    </div>
  );
}
